import React, { useState, useEffect } from "react";
import "./Crm.css";
import useCrmStore from "../../services/useCrmStore";
import useUserStore from "../../services/useUserStore";
import useBulkMessageStore from "../../services/useBulkMessageStore";
import { toast } from "react-toastify";
import CreateLeadForm from "./components/CreateLeadForm";
import LeadDetailModal from "./components/LeadDetailModal";
import FilterModal from "./components/FilterModal";
import StageManager from "./components/StageManager";
import { api } from "../../services/api";
import LeadBulkMessageModal from "./components/LeadBulkMessageModal";
import CrmCard from "./components/CrmCard";
import CrmColumn from "./components/CrmColumn";
import FullScreenLoader from "../FullScreenLoader/FullScreenLoader";
import usePaymentGuard from "../../hooks/usePaymentGuard";
import PaymentBlockModal from "../PaymentBlockModal/PaymentBlockModal";
import BulkMessageManager from "../BulkMessageManager";


import {
  FiSearch,
  FiFilter,
  FiUser,
  FiDollarSign,
  FiClock,
  FiMoreHorizontal,
  FiPlus,
  FiX,
  FiRefreshCw,
  FiSettings,
  FiPhone,
  FiMail,
  FiMessageCircle,
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";


const Crm = () => {
  const {
    stages,
    leads,
    loading,
    error,
    fetchStages,
    fetchLeads,
    moveLeadToStage,
    selectLead,
    selectedLead,
    updateLead,
    // Pagination
    pagination,
    leadsByStage: storeLeadsByStage,
    stageLoading,
    fetchLeadsByStage,
    fetchAllStagesWithLeads,
    changePageForStage,
  } = useCrmStore();

  const { users: members, fetchUsers } = useUserStore();

  // Hook para verificar se pode executar ações (requer pagamento)
  const {
    needsPayment,
    showBlockModal,
    closeBlockModal,
    showPaymentRequired,
  } = usePaymentGuard();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showStageManager, setShowStageManager] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [defaultStageId, setDefaultStageId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState({});
  const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());


  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkChannel, setBulkChannel] = useState("WHATSAPP");



  useEffect(() => {
    if (!loadedOnce) {
      fetchStages();
      fetchUsers();
      setLoadedOnce(true);
    }
  }, [loadedOnce, fetchStages, fetchUsers]);

  // Create a stable key based on stage IDs to detect when stages change
  const stageIdsKey = React.useMemo(
    () => stages.map(s => s.id).sort().join(','),
    [stages]
  );

  // Once stages are loaded, fetch leads for each stage with pagination
  useEffect(() => {
    if (loadedOnce && stages.length > 0) {
      const filters = { ...activeFilters };
      if (searchTerm) {
        filters.search = searchTerm;
      }
      fetchAllStagesWithLeads(100, filters);
    }
  }, [loadedOnce, stageIdsKey]);


  useEffect(() => {
    if (loadedOnce && stages.length > 0) {
      const filters = { ...activeFilters };
      if (searchTerm) {
        filters.search = searchTerm;
      }
      fetchAllStagesWithLeads(100, filters);
    }
  }, [activeFilters, loadedOnce, stageIdsKey]);


  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);


  useEffect(() => {
    if (!loadedOnce || stages.length === 0) return;

    const timeoutId = setTimeout(() => {
      const filters = { ...activeFilters };
      if (searchTerm) {
        filters.search = searchTerm;
      }
      fetchAllStagesWithLeads(100, filters);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Get all visible leads across all paginated stages
  const allVisibleLeads = React.useMemo(() => {
    const allLeads = [];
    Object.values(storeLeadsByStage).forEach(stageLeads => {
      if (stageLeads) {
        allLeads.push(...stageLeads);
      }
    });
    return allLeads.length > 0 ? allLeads : leads;
  }, [storeLeadsByStage, leads]);

  useEffect(() => {
    setSelectedLeadIds((prev) => {
      const visibleIds = new Set(allVisibleLeads.map((l) => l.id));
      const next = new Set();
      prev.forEach((id) => {
        if (visibleIds.has(id)) {
          next.add(id);
        }
      });
      return next;
    });
  }, [allVisibleLeads]);
  const visibleSelectedLeads = allVisibleLeads.filter((lead) =>
    selectedLeadIds.has(lead.id)
  );
  const handleToggleLeadSelection = (leadId) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) {
        next.delete(leadId);
      } else {
        next.add(leadId);
      }
      return next;
    });
  };
  const handleToggleColumnSelection = (stageId, visibleLeadIds) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);

      const allSelected = visibleLeadIds.every((id) => next.has(id));

      if (allSelected) {
        visibleLeadIds.forEach((id) => next.delete(id));
      } else {
        visibleLeadIds.forEach((id) => next.add(id));
      }

      return next;
    });
  };
  const clearLeadSelection = () => {
    setSelectedLeadIds(new Set());
  };
  const openBulkMessageModal = (channel) => {
    if (visibleSelectedLeads.length === 0) {
      toast.info("Selecione pelo menos um lead visível para enviar em massa.");
      return;
    }

    setBulkChannel(channel);
    setIsBulkModalOpen(true);
  };
  const handleCloseBulkModal = () => {
    setIsBulkModalOpen(false);
  };

  const handleApplyFilters = (filters) => {
    setActiveFilters(filters);
    setShowFilterModal(false);
    toast.success("Filtros aplicados");
  };

  const handleClearFilters = () => {
    setActiveFilters({});
    setSearchTerm("");
    toast.info("Filtros limpos");
  };

  const getActiveFilterCount = () => {
    let count = 0;
    Object.keys(activeFilters).forEach((key) => {
      if (Array.isArray(activeFilters[key])) {
        count += activeFilters[key].length;
      } else if (activeFilters[key]) {
        count += 1;
      }
    });
    if (searchTerm) count += 1;
    return count;
  };

  const handleDragStart = (e, leadId) => {
    e.dataTransfer.setData("leadId", leadId);
    e.dataTransfer.effectAllowed = "move";
    if (e.target) {
      e.dataTransfer.setDragImage(e.target, 50, 50);
    }
  };

  const handleDrop = async (leadId, newStageId) => {
    const lead = allVisibleLeads.find((l) => l.id === leadId);
    if (!lead) return;
    if (lead.stage_id === newStageId) return;

    try {
      await moveLeadToStage(leadId, newStageId);
      // Refresh both old and new stage after move
      const filters = { ...activeFilters };
      if (searchTerm) filters.search = searchTerm;
      fetchLeadsByStage(lead.stage_id, pagination[lead.stage_id]?.page || 1, 100, filters);
      fetchLeadsByStage(newStageId, pagination[newStageId]?.page || 1, 100, filters);
      toast.success("Lead movido com sucesso");
    } catch (err) {
      toast.error("Falha ao mover lead");
    }
  };

  const handleStageChange = async (leadId, newStageId) => {
    const lead = allVisibleLeads.find((l) => l.id === leadId);
    if (!lead) return;
    if (lead.stage_id === newStageId) return;

    const newStage = stages.find((s) => s.id === newStageId);
    if (!newStage) return;

    try {
      await moveLeadToStage(leadId, newStageId);
      // Refresh both old and new stage after move
      const filters = { ...activeFilters };
      if (searchTerm) filters.search = searchTerm;
      fetchLeadsByStage(lead.stage_id, pagination[lead.stage_id]?.page || 1, 100, filters);
      fetchLeadsByStage(newStageId, pagination[newStageId]?.page || 1, 100, filters);
      toast.success(`Lead movido para "${newStage.name}"`);
    } catch (err) {
      toast.error("Falha ao mover lead");
    }
  };

  const handleAssigneeChange = async (leadId, newAssigneeId) => {
    const lead = allVisibleLeads.find((l) => l.id === leadId);
    if (!lead) return;

    const assigneeIdValue = newAssigneeId ? parseInt(newAssigneeId) : null;
    if (lead.assigned_to === assigneeIdValue) return;

    try {
      await updateLead(leadId, { assigned_to: assigneeIdValue });
      const assignee = members.find((m) => m.id === assigneeIdValue);
      if (assignee) {
        const isAI = assignee.is_ai_agent;
        toast.success(
          isAI
            ? `Lead atribuído ao Agente de IA "${assignee.name}"`
            : `Lead atribuído para "${assignee.name}"`
        );
      } else {
        toast.success("Responsável removido do lead");
      }
    } catch (err) {
      toast.error("Falha ao atribuir responsável");
    }
  };

  const handleAddLead = (stageId) => {
    // Verificar se precisa de pagamento antes de permitir criar lead
    if (needsPayment) {
      showPaymentRequired();
      return;
    }
    setDefaultStageId(stageId);
    setShowCreateForm(true);
  };

  const handleCardClick = (lead) => {
    selectLead(lead);
  };

  const handleCloseModal = () => {
    selectLead(null);
  };

  const handleCloseCreateForm = () => {
    setShowCreateForm(false);
    // Refresh the stage where the lead was created
    if (defaultStageId) {
      const filters = { ...activeFilters };
      if (searchTerm) filters.search = searchTerm;
      fetchLeadsByStage(defaultStageId, 1, 100, filters);
    } else {
      // If no default stage, refresh all stages
      const filters = { ...activeFilters };
      if (searchTerm) filters.search = searchTerm;
      fetchAllStagesWithLeads(100, filters);
    }
    setDefaultStageId(null);
  };


  const handleSendMessage = async ({ channel, content, subject, toEmail, lead, integration_id, media_id }) => {
    try {
      if (channel === "WHATSAPP") {
        const response = await api.post("/api/whatsapp_evolution/send_message", {
          channel,
          content,
          lead,
          integration_id,
          media_id,
        });
      }

      if (channel === "EMAIL") {
        const payload = {
          channel,
          subject,
          content,
          to: toEmail || lead?.email,
          lead: {
            id: lead?.id,
            name: lead?.name,
            email: lead?.email,
            phone: lead?.phone,
            company: lead?.company,
          },
          integration_id,
        };

        const response = await api.post("/api/email/send_email", payload);
      }

      if (channel === "PHONE_CALL") {
        // Registro de ligação - não requer integração
      }

      toast.success("Mensagem enviada com sucesso");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao enviar mensagem");
      throw err;
    }
  };


  // Store de bulk message para envio assíncrono
  const startBulkMessage = useBulkMessageStore((state) => state.startBulkMessage);

  const handleSendBulkMessages = async (payload) => {
    try {
      // Para Email, mantém o comportamento síncrono existente
      if (payload.channel === "EMAIL") {
        const response = await api.post("/api/email/send_bulk", payload);

        if (response?.data?.success) {
          toast.success(
            `Mensagens em massa enviadas para ${response.data.batch_result?.success || 0} leads.`
          );
        } else {
          toast.error(
            response?.data?.batch_result?.error ||
            response?.data?.error ||
            "Falha ao enviar mensagens em massa."
          );
        }

        setIsBulkModalOpen(false);
        clearLeadSelection();
        return;
      }

      // Para WhatsApp - usa o sistema assíncrono com acompanhamento
      if (payload.channel === "WHATSAPP") {
        const result = await startBulkMessage(payload);

        if (result.success) {
          toast.success(
            `Envio em massa iniciado para ${payload.recipients?.length || 0} leads. Acompanhe o progresso abaixo.`
          );
          setIsBulkModalOpen(false);
          clearLeadSelection();
        } else {
          toast.error(result.error || "Falha ao iniciar envio em massa.");
        }
        return;
      }

      toast.error("Canal não suportado para envio em massa.");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao enviar mensagens em massa.");
    }
  };



  // Handle page change for a specific stage
  const handlePageChange = (stageId, newPage) => {
    const filters = { ...activeFilters };
    if (searchTerm) {
      filters.search = searchTerm;
    }
    changePageForStage(stageId, newPage, filters);
  };

  // Use paginated leads from store, fallback to filtered leads for compatibility
  const leadsByStage = {};
  stages.forEach((stage) => {
    leadsByStage[stage.id] = storeLeadsByStage[stage.id] || leads.filter((lead) => lead.stage_id === stage.id);
  });

  if (loading && !loadedOnce) {
    return (
      <div
        className="crm"
        style={{
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <div style={{ color: "var(--crm-text-main)", fontSize: "1.2rem" }}>
          < FullScreenLoader message="Carregando dados do dashboard..." />
        </div>
      </div>
    );
  }

  return (
    <div className="crm">
      <header className="crm-header">
        <div className="crm-header-main">
          <div>
            <h1 className="crm-title">Pipeline de Vendas</h1>
            <p className="crm-subtitle">
              Visualize o avanço dos seus leads em tempo real e arraste cada
              oportunidade entre as etapas do funil.
            </p>
          </div>
          <div className="crm-header-actions">
            <button
              className="crm-ghost-button"
              onClick={() => setShowStageManager(true)}
              style={{
                borderColor: "rgba(148, 163, 184, 0.5)",
                background: "rgba(15, 23, 42, 0.5)",
              }}
            >
              <FiSettings />
              Gerenciar Stages
            </button>
            <button
              className="crm-ghost-button"
              onClick={() => setShowFilterModal(true)}
              style={{
                borderColor:
                  getActiveFilterCount() > 0
                    ? "var(--crm-accent)"
                    : "rgba(148, 163, 184, 0.5)",
                background:
                  getActiveFilterCount() > 0
                    ? "rgba(56, 189, 248, 0.1)"
                    : "rgba(15, 23, 42, 0.5)",
              }}
            >
              <FiFilter />
              Filtros{" "}
              {getActiveFilterCount() > 0 &&
                `(${getActiveFilterCount()})`}
            </button>
            {getActiveFilterCount() > 0 && (
              <button
                className="crm-ghost-button"
                onClick={handleClearFilters}
                style={{ borderColor: "rgba(239, 68, 68, 0.5)" }}
              >
                <FiRefreshCw />
                Limpar
              </button>
            )}
            <button
              className="crm-primary-button"
              onClick={() => setShowCreateForm(true)}
            >
              <FiPlus />
              Novo lead
            </button>
          </div>
        </div>

        <div className="crm-header-bottom">
          <div className="crm-search-wrapper">
            <FiSearch />
            <input
              type="text"
              placeholder="Buscar por nome, email, telefone ou empresa..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--crm-text-muted)",
                  cursor: "pointer",
                  padding: "4px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <FiX />
              </button>
            )}
          </div>

          <div className="crm-header-badges">
            <div className="crm-badge">
              <FiUser />
              <span>Total: {Object.values(pagination).reduce((sum, p) => sum + (p?.total || 0), 0) || leads.length} leads</span>
            </div>
            <div className="crm-badge">
              <FiDollarSign />
              <span>Estágios: {stages.length}</span>
            </div>
          </div>
        </div>
      </header>

      <div className="crm-bulk-actions">
        <span>
          {visibleSelectedLeads.length > 0
            ? `${visibleSelectedLeads.length} lead${visibleSelectedLeads.length !== 1 ? "s" : ""} selecionado${visibleSelectedLeads.length !== 1 ? "s" : ""} nesta visualização`
            : "Nenhum lead selecionado"}
        </span>

        <div className="crm-bulk-actions-buttons">
          <button
            className="crm-ghost-button"
            onClick={() => openBulkMessageModal("WHATSAPP")}
          >
            <FaWhatsapp />
            Enviar WhatsApp em massa
          </button>

          <button
            className="crm-ghost-button"
            onClick={() => openBulkMessageModal("EMAIL")}
          >
            <FiMail />
            Enviar Email em massa
          </button>

          {visibleSelectedLeads.length > 0 && (
            <button
              className="crm-ghost-button"
              onClick={clearLeadSelection}
            >
              <FiX />
              Limpar seleção
            </button>
          )}
        </div>
      </div>

      {/* Gerenciador de envio em massa - abaixo dos botões */}
      <BulkMessageManager />

      <main className="crm-board">
        <div className="crm-columns">
          {stages.map((stage, index) => (
            <CrmColumn
              key={stage.id}
              column={stage}
              deals={leadsByStage[stage.id] || []}
              onDrop={handleDrop}
              onDragStart={handleDragStart}
              onCardClick={handleCardClick}
              onAddClick={handleAddLead}
              onSendMessage={handleSendMessage}
              selectedLeadIds={selectedLeadIds}
              onToggleLeadSelection={handleToggleLeadSelection}
              onToggleColumnSelection={handleToggleColumnSelection}
              stages={stages}
              onStageChange={handleStageChange}
              members={members}
              onAssigneeChange={handleAssigneeChange}
              pagination={pagination[stage.id]}
              onPageChange={handlePageChange}
              isLoading={stageLoading[stage.id]}
            />
          ))}
        </div>
      </main>


      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          stages={stages}
          onClose={handleCloseModal}
          onLeadUpdated={() => {
            // Refresh the stage of the updated lead
            const filters = { ...activeFilters };
            if (searchTerm) filters.search = searchTerm;
            if (selectedLead?.stage_id) {
              fetchLeadsByStage(selectedLead.stage_id, pagination[selectedLead.stage_id]?.page || 1, 100, filters);
            }
          }}
        />
      )}


      {showCreateForm && (
        <CreateLeadForm
          stages={stages}
          defaultStageId={defaultStageId}
          onClose={handleCloseCreateForm}
        />
      )}

      {showFilterModal && (
        <FilterModal
          stages={stages}
          currentFilters={activeFilters}
          onApply={handleApplyFilters}
          onClose={() => setShowFilterModal(false)}
        />
      )}

      {showStageManager && (
        <StageManager
          onClose={() => {
            setShowStageManager(false);
            // Note: fetchStages() removed - StageManager already updates the store
            // during CRUD operations. The useEffect with stageIdsKey will
            // automatically refetch leads when stages change.
          }}
        />
      )}
      {isBulkModalOpen && (
        <LeadBulkMessageModal
          channel={bulkChannel}
          leads={visibleSelectedLeads}
          onSend={handleSendBulkMessages}
          onClose={handleCloseBulkModal}
        />
      )}

      {/* Modal de bloqueio por falta de pagamento */}
      <PaymentBlockModal
        isOpen={showBlockModal}
        onClose={closeBlockModal}
        actionName="adicionar leads"
      />

    </div>
  );
};

export default Crm;
